/**
 * ===========================================================================
 * CONVERSATIONS
 * ===========================================================================
 * Internal threads between colleagues, and threads with a client.
 *
 * TWO STREAMS, DECIDED ONCE. A conversation's type is fixed when it is created and a
 * database trigger refuses any change to it. There is no control on this screen to
 * convert one into the other, because there is no such operation anywhere in the system:
 * an internal discussion cannot become client-visible by anybody's mistake. The type is
 * shown on every thread and in the composer, since the one thing a person must never be
 * unsure of is who can read what they are about to write.
 *
 * Both streams can be STARTED here, and that is the only place the two meet. The dialog
 * therefore says which one is being created in words at every step it is chosen through
 * -- the mode, the participant list, the first-message box and the confirm button all
 * name the audience, because the type is decided once and cannot be taken back.
 *
 * WHAT YOU SEE IS WHAT YOU ARE IN. The list is scoped by participation, not by matter --
 * a colleague on the same case does not see a thread they were not added to. A
 * supervising partner or an administrator may see more, and may still not post: the
 * server refuses with a reason, which this screen shows rather than hiding the box.
 *
 * POLLING, NOT SOCKETS. Every five seconds while the tab is visible. A websocket would
 * mean an ASGI server and a Redis channel layer for a gain measured in single seconds on
 * a screen people leave open. Polling stops when the tab is hidden, so an abandoned tab
 * is not a background load on the API forever.
 */

import {
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  usePaginatedList,
  userMessage,
  type AddressableColleague,
  type CaseListItem,
  type ClientDetail,
  type Conversation,
  type ConversationMessage,
  type CursorPage,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  RadioGroup,
  Select,
  Skeleton,
  Spinner,
  Textarea,
  cn,
  useToast,
} from '@legalcrm/ui';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '@/auth/AuthProvider';
import { formatDateTime } from '@/lib/format';

import { relationshipLabel } from './clients/contactOptions';

/** Long enough to feel immediate, short enough not to hammer the API. */
const POLL_MS = 5_000;

/** What to do about a matter the client cannot see. Worded once, so both places agree. */
const UNSEEN_MATTER =
  'Documents and updates filed under it will not appear in their app. Give a contact access on the client’s Portal access tab.';

export function ConversationsPage() {
  const { me } = useAuth();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);

  const list = usePaginatedList<Conversation>(
    qk.list('conversations', { screen: 'conversations' }),
    STAFF.conversations,
    {},
    { refetchInterval: POLL_MS },
  );

  const conversations = list.items ?? [];

  // Land on something rather than an empty pane, but never override a deliberate choice.
  useEffect(() => {
    if (selectedId === null && conversations.length > 0) {
      setSelectedId(conversations[0]?.id ?? null);
    }
  }, [conversations, selectedId]);

  const selected = conversations.find((c) => c.id === selectedId) ?? null;

  return (
    <div className="flex h-[calc(100vh-9rem)] flex-col gap-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-ink">Conversations</h1>
          <p className="text-sm text-ink-muted">
            Threads you are part of. A colleague on the same matter does not see a thread
            they were not added to.
          </p>
        </div>
        <Button onClick={() => setComposing(true)}>New conversation</Button>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[22rem_1fr]">
        <ThreadList
          conversations={conversations}
          loading={list.isLoading}
          error={list.error}
          selectedId={selectedId}
          currentUserId={me?.user.id ?? null}
          onSelect={setSelectedId}
        />
        {selected ? (
          <Thread conversation={selected} currentUserId={me?.user.id ?? null} />
        ) : (
          <Card className="flex min-h-0 items-center justify-center">
            <EmptyState
              title="Nothing selected"
              description="Choose a conversation, or start one."
            />
          </Card>
        )}
      </div>

      <NewConversationModal
        open={composing}
        onClose={() => setComposing(false)}
        onStarted={(id) => {
          setSelectedId(id);
          setComposing(false);
        }}
      />
    </div>
  );
}

function ThreadList({
  conversations,
  loading,
  error,
  selectedId,
  currentUserId,
  onSelect,
}: {
  conversations: Conversation[];
  loading: boolean;
  error: unknown;
  selectedId: string | null;
  currentUserId: string | null;
  onSelect: (id: string) => void;
}) {
  if (error) {
    return (
      <Card className="min-h-0">
        <ErrorState title="Conversations could not be loaded" />
      </Card>
    );
  }

  return (
    <Card className="flex min-h-0 flex-col overflow-hidden">
      <CardBody className="min-h-0 flex-1 overflow-y-auto p-0">
        {loading && conversations.length === 0 ? (
          <div className="flex flex-col gap-2 p-3">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="p-6">
            <EmptyState
              title="No conversations yet"
              description="Start one with a colleague, or with a client."
            />
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {conversations.map((conversation) => (
              <li key={conversation.id}>
                <button
                  type="button"
                  onClick={() => onSelect(conversation.id)}
                  aria-current={conversation.id === selectedId}
                  className={cn(
                    'flex w-full flex-col gap-1 px-4 py-3 text-left transition-colors',
                    'hover:bg-surface-sunken',
                    conversation.id === selectedId && 'bg-surface-sunken',
                  )}
                >
                  <span className="flex items-center gap-2">
                    {/* Never colour alone: the dot is paired with bold text, and the
                        stream is named in words next to it. */}
                    {conversation.has_unread && (
                      <span
                        aria-label="Unread"
                        className="h-2 w-2 shrink-0 rounded-full bg-primary"
                      />
                    )}
                    <span
                      className={cn(
                        'truncate text-sm text-ink',
                        conversation.has_unread && 'font-semibold',
                      )}
                    >
                      {conversation.title || 'Untitled conversation'}
                    </span>
                  </span>
                  <span className="truncate text-xs text-ink-muted">
                    {otherParticipants(conversation, currentUserId).join(', ') ||
                      'Only you'}
                  </span>
                  <span className="flex items-center gap-2">
                    <StreamBadge type={conversation.conversation_type} />
                    <span className="truncate text-xs text-ink-muted">
                      {conversation.last_message_at
                        ? formatDateTime(conversation.last_message_at)
                        : 'No messages yet'}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Everybody in the thread except the reader, as a readable list.
 *
 * Without this a thread showed a title and nothing else, so after creating one there was
 * no way to see who you were talking to -- the single most important fact on the screen,
 * because it decides what you are willing to say.
 *
 * The reader is dropped from the list: "Verification thread — you, Alex" is noise, and on
 * a two-person thread the only name that matters is the other one.
 */
function otherParticipants(
  conversation: Conversation,
  currentUserId: string | null,
  { withEmail = false }: { withEmail?: boolean } = {},
): string[] {
  return conversation.participants
    .filter((p) => !(p.participant_type === 'user' && p.participant_id === currentUserId))
    .map((p) => (withEmail && p.email ? `${p.name} (${p.email})` : p.name))
    .filter(Boolean);
}

/** The stream, in words. A client thread and an internal one must never be told apart by
 *  colour alone -- getting this wrong is the whole failure mode of the feature. */
function StreamBadge({ type }: { type: Conversation['conversation_type'] }) {
  if (type === 'client') {
    return <Badge tone="warning">Client can read this</Badge>;
  }
  if (type === 'system') {
    return <Badge tone="neutral">System</Badge>;
  }
  return <Badge tone="internal">Internal only</Badge>;
}

function Thread({
  conversation,
  currentUserId,
}: {
  conversation: Conversation;
  currentUserId: string | null;
}) {
  const api = useApi();
  const { toast } = useToast();
  const { matterLabel } = useAuth();
  const [body, setBody] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  // The header carries addresses; list rows carry names only, where space is short.
  const participants = otherParticipants(conversation, currentUserId, { withEmail: true });

  const messages = useApiQuery<{ results: ConversationMessage[] }>(
    qk.sub('conversations', conversation.id, 'messages'),
    STAFF.conversationMessages(conversation.id),
    { refetchInterval: POLL_MS },
  );

  // The server pages newest-first so a long thread can be opened without fetching all of
  // it. Reading order is the other way round.
  const ordered = useMemo(
    () => [...(messages.data?.results ?? [])].reverse(),
    [messages.data?.results],
  );

  const markRead = useApiMutation<void, void>(
    {
      mutationFn: () => api.post<void>(`${STAFF.conversationRead(conversation.id)}/`),
      invalidates: [qk.all('conversations')],
    },
    {},
  );

  // Opening a thread is reading it. Deliberately not tied to scrolling: a short thread is
  // read without ever scrolling, and it should not stay bold afterwards.
  const markReadRef = useRef(markRead.mutate);
  markReadRef.current = markRead.mutate;
  useEffect(() => {
    if (conversation.has_unread) markReadRef.current();
  }, [conversation.id, conversation.has_unread]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [ordered.length]);

  const send = useApiMutation<ConversationMessage, void>(
    {
      mutationFn: () =>
        api.post<ConversationMessage>(
          `${STAFF.conversationMessages(conversation.id)}/`,
          { body },
        ),
      invalidates: [qk.all('conversations')],
    },
    {
      onSuccess: (message) => {
        setBody('');
        if (message.client_can_see === false) {
          toast({
            title: `Sent, but the client cannot see this ${matterLabel.toLowerCase()} yet`,
            description: UNSEEN_MATTER,
            tone: 'warning',
          });
        }
      },
      onError: (error) => {
        // A refusal here is meaningful: an administrator can see this thread and still not
        // be in it. `userMessage` shows the server's own wording for a 409 and a 403,
        // because that wording says what to do -- ask somebody in it to add you.
        toast({
          title: 'The message was not sent',
          description: userMessage(error),
          tone: 'warning',
        });
      },
    },
  );

  return (
    <Card className="flex min-h-0 flex-col overflow-hidden">
      <CardHeader
        title={conversation.title || 'Untitled conversation'}
        description={
          participants.length > 0
            ? `With ${participants.join(', ')}`
            : 'Only you are in this conversation.'
        }
        actions={<StreamBadge type={conversation.conversation_type} />}
      />

      <CardBody className="min-h-0 flex-1 overflow-y-auto">
        {messages.isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner label="Loading messages" />
          </div>
        ) : ordered.length === 0 ? (
          <EmptyState title="No messages yet" description="Say something to begin." />
        ) : (
          <ol className="flex flex-col gap-3">
            {ordered.map((message) => {
              const mine = message.sender_id === currentUserId;
              return (
                <li
                  key={message.id}
                  className={cn('flex flex-col gap-1', mine && 'items-end')}
                >
                  {/* Attributed, and only when it is not yours. A thread of unattributed
                      bubbles is unreadable the moment a third person joins, and labelling
                      your own messages "you" is noise on every line. */}
                  {!mine && (
                    <span className="text-xs font-medium text-ink-muted">
                      {message.sender_name}
                    </span>
                  )}
                  <div
                    className={cn(
                      'max-w-[42rem] whitespace-pre-wrap rounded px-3 py-2 text-sm',
                      mine
                        ? 'bg-primary text-primary-ink'
                        : 'bg-surface-sunken text-ink',
                    )}
                  >
                    {message.body}
                  </div>
                  <span className="text-xs text-ink-muted">
                    {formatDateTime(message.sent_at)}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
        <div ref={bottomRef} />
      </CardBody>

      <div className="border-t border-line p-3">
        <Textarea
          label="Message"
          hideLabel
          rows={2}
          placeholder={
            conversation.conversation_type === 'client'
              ? 'Your client can read this thread.'
              : 'Internal only — the client cannot read this thread.'
          }
          value={body}
          onChange={(event) => setBody(event.target.value)}
        />
        <div className="mt-2 flex justify-end">
          <Button
            onClick={() => send.mutate()}
            loading={send.isPending}
            disabled={body.trim().length === 0}
          >
            Send
          </Button>
        </div>
      </div>
    </Card>
  );
}

/** Which stream is being opened. Chosen once, here, and never again anywhere. */
type Stream = 'internal' | 'client';

/**
 * Starting a thread, in either stream.
 *
 * THIS DIALOG USED TO REFUSE THE CLIENT STREAM ENTIRELY, on the reasoning that a
 * client thread is far more consequential to create by accident. The reasoning was
 * right and the conclusion was wrong: with no other surface that creates one, the
 * client app had an empty inbox by construction. So the answer is not to hide the
 * choice but to make it impossible to make without noticing.
 *
 * Every step therefore names its audience IN WORDS, never by colour or icon alone: the
 * mode is a labelled radio pair, the participants sit inside a card whose banner reads
 * "Visible to the client" or "Internal only", the first-message box carries the same
 * placeholder as the thread composer, and the confirm button says which stream it opens.
 *
 * A CLIENT THREAD BELONGS TO A MATTER. Picking the matter first is what makes the
 * contact list an answer to "who acts for this client" rather than a firm-wide list of
 * names, and it is also what the thread is filed under afterwards.
 *
 * Exported for the test beside this file: the rule it guards -- that an internal thread
 * can never leave here carrying a client contact -- fails silently and cannot be undone.
 */
export function NewConversationModal({
  open,
  onClose,
  onStarted,
}: {
  open: boolean;
  onClose: () => void;
  onStarted: (id: string) => void;
}) {
  const api = useApi();
  const { toast } = useToast();
  const { matterLabel } = useAuth();
  const [stream, setStream] = useState<Stream>('internal');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [withIds, setWithIds] = useState<string[]>([]);
  const [caseId, setCaseId] = useState('');
  const [contactIds, setContactIds] = useState<string[]>([]);

  // Internal is the opening position deliberately. Reopening the dialog must not
  // inherit the last thread's audience from a previous decision nobody is looking at.
  useEffect(() => {
    if (open) {
      setStream('internal');
      setTitle('');
      setBody('');
      setWithIds([]);
      setCaseId('');
      setContactIds([]);
    }
  }, [open]);

  // NOT `/users/`. That needs `user.view`, which only administrators and partners
  // hold, so this list was empty for a lawyer or a paralegal and most of the firm could
  // not start a conversation with anyone -- with nothing on screen explaining why.
  const colleagues = useApiQuery<AddressableColleague[]>(
    qk.list('conversations', { screen: 'addressable' }),
    STAFF.conversationsAddressable,
    { enabled: open },
  );

  // The first page, as every other matter picker in this app does. A firm-sized
  // caseload needs search-as-you-type, which is a combobox and a different component --
  // not a select quietly showing the first hundred of five hundred as though that were
  // all of them.
  const matters = useApiQuery<CursorPage<CaseListItem>>(
    qk.list('cases', { picker: 'conversations' }),
    STAFF.cases,
    { query: { limit: 100 }, enabled: open && stream === 'client' },
  );
  const matter = (matters.data?.results ?? []).find((row) => row.id === caseId) ?? null;

  // The client record carries its own contacts, so this is one request rather than a
  // second one against `/clients/{id}/contacts/`. Gated on `client.view`, which a
  // lawyer and a paralegal hold for a client they are on a matter for -- the resolver
  // reaches a client through its cases. `/users/` was the trap here once; this is not
  // the same shape of mistake.
  const client = useApiQuery<ClientDetail>(
    qk.detail('clients', matter?.client ?? 'none'),
    STAFF.client(matter?.client ?? 'none'),
    { enabled: open && stream === 'client' && Boolean(matter) },
  );
  const contacts = client.data?.contacts ?? [];

  // A contact acts for one client. A tick surviving a change of matter would put one
  // family into another family's thread, which is the disclosure this screen exists to
  // prevent.
  useEffect(() => setContactIds([]), [caseId]);

  const toggle = (setter: typeof setWithIds) => (id: string, checked: boolean) =>
    setter((current) =>
      checked ? [...current, id] : current.filter((value) => value !== id),
    );

  const start = useApiMutation<Conversation, void>(
    {
      mutationFn: () =>
        api.post<Conversation>(
          `${STAFF.conversations}/`,
          stream === 'client'
            ? {
                conversation_type: 'client',
                // Filed against the matter, which is also what decided whose contacts
                // were on offer above.
                subject_type: 'case',
                subject_id: caseId,
                participants: [
                  ...contactIds.map((id) => ({
                    participant_type: 'client_contact',
                    participant_id: id,
                  })),
                  ...withIds.map((id) => ({
                    participant_type: 'user',
                    participant_id: id,
                  })),
                ],
                title,
                body,
              }
            : {
                // Built from `withIds` alone. A contact ticked before the mode was
                // changed back is structurally absent from this payload rather than
                // filtered out of it: the server would refuse it, but an internal thread
                // must not depend on the server catching a client inside one.
                conversation_type: 'internal',
                participants: withIds.map((id) => ({
                  participant_type: 'user',
                  participant_id: id,
                })),
                title,
                body,
              },
        ),
      invalidates: [qk.all('conversations')],
    },
    {
      onSuccess: (conversation) => {
        // Started either way. A client reading a thread about a matter missing from their
        // app is the confusion this heads off, and the server is the one that knows.
        if (conversation.client_can_see === false) {
          toast({
            title: `Started, but the client cannot see this ${matterLabel.toLowerCase()} yet`,
            description: UNSEEN_MATTER,
            tone: 'warning',
          });
        }
        onStarted(conversation.id);
      },
      onError: (error) =>
        // Never a bare "it did not work". The first version of this said exactly that,
        // and swallowed a 403 whose meaning -- the role is missing a permission -- was
        // the entire diagnosis. An error the user cannot act on wastes their time and
        // mine. The 409 for a client thread with no client in it is written for the
        // reader too, and arrives here verbatim.
        toast({
          title: 'The conversation was not started',
          description: userMessage(error),
          tone: 'warning',
        }),
    },
  );

  const ready =
    body.trim().length > 0 &&
    (stream === 'client'
      ? Boolean(caseId) && contactIds.length > 0
      : withIds.length > 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        stream === 'client'
          ? 'Start a conversation with the client'
          : 'Start an internal conversation'
      }
      description={
        stream === 'client'
          ? 'The client contacts you choose can read every message in it.'
          : 'Only the colleagues you choose can see it. The client cannot.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={start.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => start.mutate()}
            loading={start.isPending}
            disabled={!ready}
          >
            {stream === 'client'
              ? 'Start a thread the client can read'
              : 'Start an internal thread'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <RadioGroup
          name="conversation-stream"
          legend="Who is this conversation with?"
          hint="Decided now and fixed. A thread never moves from one stream to the other."
          value={stream}
          onChange={(value) => setStream(value as Stream)}
          options={[
            {
              value: 'internal',
              label: 'Colleagues only',
              hint: 'Internal — the client cannot read this thread.',
            },
            {
              value: 'client',
              label: `The client, on one ${matterLabel.toLowerCase()}`,
              hint: 'Client-visible — the contacts you choose read every message in it.',
            },
          ]}
        />

        <Input
          label="Title"
          hint="What this is about. Shown in the list."
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />

        {stream === 'internal' ? (
          // The card's own banner reads "Internal only", so the audience is stated
          // beside the names being ticked rather than only at the top of the dialog.
          <Card sensitivity="internal">
            <CardBody>
              <fieldset className="flex flex-col gap-2">
                <legend className="text-sm font-medium text-ink">With</legend>
                <p className="text-xs text-ink-muted">
                  Nobody outside the firm can be added to an internal thread, now or
                  later.
                </p>
                <ColleaguePicker
                  colleagues={colleagues.data}
                  loading={colleagues.isLoading}
                  selected={withIds}
                  onToggle={toggle(setWithIds)}
                />
              </fieldset>
            </CardBody>
          </Card>
        ) : (
          <>
            <Select
              label={matterLabel}
              hint={`A client thread belongs to one ${matterLabel.toLowerCase()}. The contacts offered are the ones acting for its client.`}
              placeholder={
                matters.isLoading
                  ? 'Loading…'
                  : `Choose a ${matterLabel.toLowerCase()}`
              }
              options={(matters.data?.results ?? []).map((row) => ({
                value: row.id,
                label: `${row.case_number} — ${row.title} · ${row.client_name}`,
              }))}
              value={caseId}
              onChange={(event) => setCaseId(event.target.value)}
            />

            {matters.error ? (
              // Said plainly rather than as an empty picker. "Nothing to choose from"
              // and "the list could not be fetched" call for different actions.
              <p className="text-sm text-ink-muted">{userMessage(matters.error)}</p>
            ) : !matters.isLoading && (matters.data?.results ?? []).length === 0 ? (
              <p className="text-sm text-ink-muted">
                You are not on any {matterLabel.toLowerCase()} yet, so there is no
                client to write to. Ask to be added to the team on one.
              </p>
            ) : null}

            {matter && (
              // Everything inside this card is client-readable, and the card says so in
              // its own banner. The paragraph is the sentence somebody has to have read
              // before ticking a name, so it sits above the names and not in a tooltip.
              <Card sensitivity="client-facing">
                <CardBody className="flex flex-col gap-4">
                  <p className="text-sm text-ink">
                    Everyone ticked here can read{' '}
                    <strong className="font-semibold">every message in this thread</strong>
                    , from the first one onwards. A conversation cannot be made internal
                    afterwards — there is no such operation anywhere in this system. If
                    any of it has to be said between colleagues, start an internal
                    conversation instead.
                  </p>

                  <fieldset className="flex flex-col gap-2">
                    <legend className="text-sm font-medium text-ink">
                      Client contacts — people outside the firm
                    </legend>
                    <div className="max-h-40 overflow-y-auto rounded border border-line p-2">
                      {client.isLoading ? (
                        <Skeleton className="h-16 w-full" />
                      ) : client.error ? (
                        // Distinguished from "this client has nobody on file", which is
                        // what an error rendered as an empty list would look like --
                        // and would invite starting a client thread with no client in
                        // it, which is the one thing the server refuses.
                        <p className="p-2 text-sm text-ink-muted">
                          {userMessage(client.error)}
                        </p>
                      ) : contacts.length === 0 ? (
                        <p className="p-2 text-sm text-ink-muted">
                          {matter.client_name} has no contacts recorded, so there is
                          nobody to open a client thread with. Add one on the client
                          first.
                        </p>
                      ) : (
                        contacts.map((contact) => (
                          <Checkbox
                            key={contact.id}
                            label={`${contact.full_name} — ${contact.email || 'no email address'}`}
                            hint={
                              contact.is_primary
                                ? 'Primary contact for this client'
                                : relationshipLabel(contact.relationship)
                            }
                            checked={contactIds.includes(contact.id)}
                            onChange={(event) =>
                              toggle(setContactIds)(contact.id, event.target.checked)
                            }
                          />
                        ))
                      )}
                    </div>
                  </fieldset>

                  <fieldset className="flex flex-col gap-2">
                    <legend className="text-sm font-medium text-ink">
                      Colleagues to include
                    </legend>
                    <p className="text-xs text-ink-muted">
                      Optional. They read the same thread the client reads — nothing
                      written in it is internal.
                    </p>
                    <ColleaguePicker
                      colleagues={colleagues.data}
                      loading={colleagues.isLoading}
                      selected={withIds}
                      onToggle={toggle(setWithIds)}
                    />
                  </fieldset>
                </CardBody>
              </Card>
            )}
          </>
        )}

        <Textarea
          label="First message"
          rows={3}
          // The composer's exact wording. A person moving between the two boxes should
          // be reading the same sentence about the same audience.
          placeholder={
            stream === 'client'
              ? 'Your client can read this thread.'
              : 'Internal only — the client cannot read this thread.'
          }
          value={body}
          onChange={(event) => setBody(event.target.value)}
        />
      </div>
    </Modal>
  );
}

/**
 * The firm's directory as tick boxes.
 *
 * Deliberately says nothing about who will be able to read the thread: it appears in
 * both modes, and the card around it is what names the audience. A component that
 * carried its own claim about visibility would be wrong in one of the two places.
 */
function ColleaguePicker({
  colleagues,
  loading,
  selected,
  onToggle,
}: {
  colleagues: AddressableColleague[] | undefined;
  loading: boolean;
  selected: string[];
  onToggle: (id: string, checked: boolean) => void;
}) {
  const members = colleagues ?? [];
  return (
    <div className="max-h-40 overflow-y-auto rounded border border-line p-2">
      {loading ? (
        <Skeleton className="h-24 w-full" />
      ) : members.length === 0 ? (
        <p className="p-2 text-sm text-ink-muted">
          Nobody else is active at this firm yet.
        </p>
      ) : (
        members.map((member) => (
          <Checkbox
            key={member.user_id}
            // The address, not just the display name. Two colleagues can share a
            // name, and several seeded accounts are named after their role -- which
            // made a list of people read like a list of roles.
            label={`${member.full_name} — ${member.email} · ${member.role_name}`}
            checked={selected.includes(member.user_id)}
            onChange={(event) => onToggle(member.user_id, event.target.checked)}
          />
        ))
      )}
    </div>
  );
}
